import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { getErrorMessage } from '../../docs/utils/docsUtils';
import { formatAdminNumber } from '../../../../utils/numberFormat';
import { AddTrackedFeatureDialog } from '../components/AddTrackedFeatureDialog';
import { DeleteTrackedFeatureDialog } from '../components/DeleteTrackedFeatureDialog';
import { EditTrackedFeatureDialog } from '../components/EditTrackedFeatureDialog';
import { useTrackedFeatures, useUpdateTrackedFeature } from '../hooks';
import type { FeatureKind, TrackedFeature } from '../types';

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const KIND_FILTERS: { value: FeatureKind | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'route', label: 'Routes' },
  { value: 'event', label: 'Events' },
  { value: 'tool', label: 'Tools' },
];

const text = {
  add: 'Add service to track',
  empty: 'No tracked features match this filter.',
  intro:
    'Choose which services count towards the usage charts. Each call is one count; turning a feature off stops counting but keeps its history.',
  load: 'Unable to load tracked features.',
  title: 'Feature Tracking',
} as const;

export const FeatureTrackingPage = () => {
  const [kind, setKind] = useState<FeatureKind | undefined>();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState<number>(PAGE_SIZE_OPTIONS[1]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<TrackedFeature | null>(null);
  const [deleting, setDeleting] = useState<TrackedFeature | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const list = useTrackedFeatures({ kind, page: page + 1, limit });
  const update = useUpdateTrackedFeature();

  const toggle = (feature: TrackedFeature, isActive: boolean) =>
    update.mutate(
      { publicId: feature.publicId, input: { isActive } },
      {
        onSuccess: () => setToast(`${feature.name} ${isActive ? 'is now tracked' : 'is paused'}`),
        onError: (error) => setToast(getErrorMessage(error)),
      }
    );

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{text.title}</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-slate-400">{text.intro}</p>
        </div>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
          {text.add}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by type">
        {KIND_FILTERS.map((filter) => (
          <Chip
            key={filter.label}
            label={filter.label}
            color={kind === filter.value ? 'primary' : 'default'}
            variant={kind === filter.value ? 'filled' : 'outlined'}
            onClick={() => {
              setKind(filter.value);
              setPage(0);
            }}
          />
        ))}
      </div>

      {list.isError && <Alert severity="error">{getErrorMessage(list.error, text.load)}</Alert>}

      <div className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Type</TableCell>
                <TableCell className="hidden md:table-cell">Match key</TableCell>
                <TableCell align="right">7 days</TableCell>
                <TableCell align="center">Tracked</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Skeleton variant="rounded" height={120} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="!py-10 text-gray-500">
                    {text.empty}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((feature) => (
                  <TableRow key={feature.publicId} hover>
                    <TableCell className="font-medium">{feature.name}</TableCell>
                    <TableCell>{feature.groupCode}</TableCell>
                    <TableCell>
                      <Chip size="small" label={feature.kind} variant="outlined" />
                    </TableCell>
                    <TableCell className="hidden max-w-xs break-all font-mono text-xs md:table-cell">
                      {feature.matchKey}
                    </TableCell>
                    <TableCell align="right" className="tabular-nums">
                      {formatAdminNumber(feature.count7d)}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={feature.isActive}
                        onChange={(event) => toggle(feature, event.target.checked)}
                        inputProps={{ 'aria-label': `Track ${feature.name}` }}
                      />
                    </TableCell>
                    <TableCell align="right" className="whitespace-nowrap">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => setEditing(feature)}
                          aria-label={`Edit ${feature.name}`}
                        >
                          <Pencil size={16} />
                        </IconButton>
                      </Tooltip>
                      {!feature.isSystem && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleting(feature)}
                            aria-label={`Delete ${feature.name}`}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={list.data?.total ?? 0}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setLimit(Number(event.target.value));
            setPage(0);
          }}
        />
      </div>

      <AddTrackedFeatureDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(name) => setToast(`${name} is now tracked`)}
      />
      <EditTrackedFeatureDialog
        feature={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setToast('Feature updated')}
      />
      <DeleteTrackedFeatureDialog
        feature={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(name) => setToast(`${name} deleted`)}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </div>
  );
};
